// stores/useEmailTemplateStore.ts

import { defineStore } from 'pinia';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  BranchDTO,
  BranchYearDTO,
  IBranchDTO,
  IBranchYearDTO,
  IPaginationDTO,
  ITeamDTO,
  IUserDTO,
  PaginationDTO,
  TeamDTO,
  UserDTO,
  YearDTO,
} from '@/dtos';
import {
  addAdminsToBranchAPI,
  addBranchToYearAPI,
  getBranchAdminsAPI,
  getBranchesForYearAdminAPI,
  getBranchYearAPI,
  getBranchYearsAPI,
  getPossibleBranchAdminsAPI,
  getPossibleBranchYearsAPI,
  getTeamsForBranchYearAPI,
  removeAdminFromBranchAPI,
  removeBranchFromYearAPI,
  updateBranchYearAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from '../updateStatus';
import { computed, Ref } from 'vue';
import { useBranchStore } from './branch';
import { useAuthStore } from '..';
import { getBranchesForYearAPI } from '@/services';

export const useBranchYearStore = defineStore('branchYear', () => {
  const statusStore = useUpdateStatusStore();
  const branchStore = useBranchStore();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  const getAllBranchYearsQuery = (branchId: Ref<string>, pagination: Ref<PaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-years', branchId.value, pagination.value]),
      queryFn: async (): Promise<{ data: IBranchYearDTO[]; count: number }> => {
        const ret = await getBranchYearsAPI(branchId.value, pagination.value);
        const branchYears = await Promise.all<IBranchYearDTO[]>(
          ret.data.map((branchYear) => new BranchYearDTO(branchYear)),
        );
        return { data: branchYears, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && authStore.isAdmin),
    });
  const getPossibleBranchYearsQuery = (branchId: Ref<string>, pagination: Ref<PaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-years-possible', branchId.value, pagination.value]),
      queryFn: async () => {
        const ret = await getPossibleBranchYearsAPI(branchId.value, pagination.value);
        const branchYears = await Promise.all(
          ret.data.map((branchYear) => new YearDTO(branchYear)),
        );
        return { data: branchYears, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && authStore.isAdmin),
    });

  const getBranchYearTeamsQuery = (
    branchId: Ref<string>,
    yearId: Ref<string>,
    pagination: Ref<PaginationDTO>,
  ) =>
    useQuery({
      queryKey: computed(() => [
        'branch-year-teams',
        branchId.value,
        yearId.value,
        pagination.value,
      ]),
      queryFn: async (): Promise<{ data: ITeamDTO[]; count: number }> => {
        const ret = await getTeamsForBranchYearAPI(branchId.value, yearId.value, pagination.value);
        const branchYearTeams = await Promise.all<ITeamDTO[]>(
          ret.data.map((team) => new TeamDTO(team)),
        );
        return { data: branchYearTeams, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && !!yearId.value && authStore.isAdmin),
    });

  const getBranchYearQuery = (branchId: Ref<string>, yearId: Ref<string>) =>
    useQuery({
      queryKey: computed(() => ['branch-year', branchId, yearId]),
      queryFn: async (): Promise<BranchYearDTO> => {
        const ret = await getBranchYearAPI(branchId.value, yearId.value);
        return new BranchYearDTO(ret);
      },
      enabled: computed(() => !!branchId.value && !!yearId.value),
    });
  const getBranchesForYearQuery = (yearId?: Ref<string>, pagination?: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-year', yearId?.value, pagination?.value]),
      queryFn: async (): Promise<{ data: IBranchDTO[]; count: number }> => {
        let ret;
        if (authStore.isAdmin && yearId?.value) {
          let pageOptions;
          if (!pagination?.value) {
            pageOptions = new PaginationDTO();
          } else {
            pageOptions = pagination?.value;
          }
          ret = await getBranchesForYearAdminAPI(yearId.value, pageOptions);
        } else {
          ret = await getBranchesForYearAPI();
        }

        const branches = await Promise.all(ret.data.map((branch) => new BranchDTO(branch)));
        return { data: branches, count: ret.count };
      },
    });

  const getBranchAdminsQuery = (branchId: Ref<string>, pagination: Ref<PaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-admins', branchId.value, pagination.value]),
      queryFn: async (): Promise<{ data: IUserDTO[]; count: number }> => {
        const ret = await getBranchAdminsAPI(branchId.value, pagination.value);
        const branchAdmins = await Promise.all(
          ret.data.map((branchAdmin) => new UserDTO(branchAdmin)),
        );
        return { data: branchAdmins, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && authStore.isAdmin),
    });

  const getPossibleBranchAdminsQuery = (branchId: Ref<string>, pagination: Ref<PaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-admins-possible', branchId.value, pagination.value]),
      queryFn: async (): Promise<{ data: IUserDTO[]; count: number }> => {
        const ret = await getPossibleBranchAdminsAPI(branchId.value, pagination.value);
        const branchAdmins = await Promise.all(
          ret.data.map((branchAdmin) => new UserDTO(branchAdmin)),
        );
        return { data: branchAdmins, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && authStore.isAdmin),
    });

  const update = useMutation({
    mutationFn: (branch: IBranchYearDTO) => {
      return updateBranchYearAPI(branch);
    },
    onSuccess: (data: IBranchYearDTO) => {
      //TODO need to invalidate getAllBranchYearsQuery
      queryClient.invalidateQueries({ queryKey: ['branch-year', data.branch?.id] });
      const key = `branch-year-${data.id}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-year-${variables.id}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const addToBranchYear = useMutation({
    mutationFn: ({ branchId, ids }: { branchId: string; ids: string[] }) => {
      return addBranchToYearAPI(branchId, ids);
    },
    onSuccess: (data: boolean, variables) => {
      branchStore.invalidate(variables.branchId);
      queryClient.invalidateQueries({ queryKey: ['branch-years'] });

      const key = `branch-year-${variables.branchId}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-year-${variables.branchId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const removeBranchYear = useMutation({
    mutationFn: ({ branchId, yearId }: { branchId: string; yearId: string }) => {
      return removeBranchFromYearAPI(branchId, yearId);
    },
    onSuccess: (data: boolean, variables) => {
      branchStore.invalidate(variables.branchId);
      queryClient.invalidateQueries({ queryKey: ['branch-years'] });
      const key = `branch-year-${variables.branchId}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-year-${variables.branchId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const addAdminToBranchYear = useMutation({
    mutationFn: ({ branchId, ids }: { branchId: string; ids: string[] }) => {
      return addAdminsToBranchAPI(branchId, ids);
    },
    onSuccess: (data: boolean, variables) => {
      branchStore.invalidate(variables.branchId);
      queryClient.invalidateQueries({ queryKey: ['branch-admins-possible'] });
      queryClient.invalidateQueries({ queryKey: ['branch-admins'] });
      const key = `branch-year-admin-add`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string) => {
      const key = `branch-year-admin-add`;
      statusStore.setErrorStatus(key, err);
    },
  });
  const removeBranchYearAdmin = useMutation({
    mutationFn: ({ branchId, userId }: { branchId: string; userId: string }) => {
      return removeAdminFromBranchAPI(branchId, userId);
    },
    onSuccess: (data: boolean, variables) => {
      branchStore.invalidate(variables.branchId);
      queryClient.invalidateQueries({ queryKey: ['branch-admins-possible'] });
      queryClient.invalidateQueries({ queryKey: ['branch-admins'] });
      const key = `branch-year-admin-${variables.userId}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-year-admin-${variables.branchId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  return {
    getAllBranchYearsQuery,
    getPossibleBranchYearsQuery,
    getBranchesForYearQuery,
    getBranchAdminsQuery,
    getBranchYearQuery,
    getBranchYearTeamsQuery,
    getPossibleBranchAdminsQuery,
    update,
    addToBranchYear,
    removeBranchYear,
    addAdminToBranchYear,
    removeBranchYearAdmin,
  };
});
